/**
 * MagnetAccessPage — /m/:slug/get?t=<token>
 *
 * A Free member's topic email links here (team job mct_cebd6a68-c62): the
 * magnet's ACCESS PAGE — the same thank-you page a person sees after opting
 * in, with the download and the call-to-action paths — and no form. The
 * worker (POST /api/public/magnets/:slug/access, worker/src/magnet-access.ts)
 * checks the signed link, records the visit as an access (never an opt-in)
 * and returns the page.
 *
 * The token is a personal link, so: it comes off the address bar before
 * anything else runs, the page sends no referrer (meta tag here, the
 * Referrer-Policy header in public/_headers), and no tag manager loads. The
 * page never names the visitor, their address or any membership. A link that
 * is expired or not ours shows one friendly screen offering a fresh link by
 * email; the answer to that request never says whether anything was sent.
 *
 * Works while the magnet is still coming soon. With no thank-you asset yet it
 * shows a minimal page: the title, the download and the book-time button.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import MagnetHeader from './components/MagnetHeader';
import MagnetFooter from './components/MagnetFooter';
import { MagnetStructuredThankyou, type MagnetFunnelUrls } from './components/MagnetStructured';
import { parseMagnetThankyouContent } from './lib/magnetContent';
import { CtaStack, isFullDoc, structuredRenderEnabled, type CtaPayload, type MagnetBrand, type MagnetTalkTo } from './MagnetPage';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const ACCENT = '#F05A28';
const MUTED = '#6b7280';

interface AccessPayload {
  lead_magnet: {
    slug: string; title: string; badge: string;
    source_book_title: string | null; source_book_author: string | null;
    thankyou_html: string | null; thankyou_content: unknown;
  };
  download_url: string | null;
  book_call: MagnetTalkTo | null;
  cta: CtaPayload;
  cover_url: string | null;
  funnel_urls: MagnetFunnelUrls;
  brand: MagnetBrand | null;
}

type Phase =
  | { phase: 'loading' }
  | { phase: 'ready'; data: AccessPayload }
  | { phase: 'expired' }
  | { phase: 'not_available' }
  | { phase: 'error' };

/** Read ?t= once and take it off the address bar at once (history only; no
 *  reload). Pure enough to call from a state initialiser. */
function takeToken(): string {
  try {
    const u = new URL(window.location.href);
    const t = u.searchParams.get('t') ?? '';
    if (t) {
      u.searchParams.delete('t');
      window.history.replaceState(window.history.state, '', u.pathname + (u.search ? u.search : '') + u.hash);
    }
    return t;
  } catch {
    return '';
  }
}

const button: React.CSSProperties = {
  display: 'inline-block', padding: '12px 22px', borderRadius: 8, border: 'none', background: ACCENT,
  color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', textDecoration: 'none',
};
const secondary: React.CSSProperties = { ...button, background: '#fff', color: ACCENT, border: `2px solid ${ACCENT}` };
const box: React.CSSProperties = { maxWidth: 560, margin: '24px auto', padding: '20px 24px', border: '1px solid #E5E7EB', borderRadius: 10, background: '#fff', textAlign: 'center' };

function DownloadBox({ url }: { url: string }) {
  return (
    <div style={box} data-testid="magnet-access-download">
      <a href={url} rel="noreferrer" style={button}>Download your copy</a>
    </div>
  );
}

function Expired({ slug, token }: { slug: string; token: string }) {
  const [sent, setSent] = useState<'no' | 'busy' | 'yes'>('no');
  async function ask() {
    setSent('busy');
    try {
      await fetch(`${API_BASE}/api/public/magnets/${encodeURIComponent(slug)}/access/fresh-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token }),
      });
    } catch { /* the answer is the same either way */ }
    setSent('yes');
  }
  return (
    <div style={{ maxWidth: 560, margin: '64px auto', padding: '0 24px' }} data-testid="magnet-access-expired">
      <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>This link has expired</h1>
      {sent === 'yes'
        ? <p style={{ lineHeight: 1.6 }}>If this link is yours, a fresh one is on its way to your inbox.</p>
        : (
          <>
            <p style={{ lineHeight: 1.6 }}>For your security, these links stop working after a while. We can email a fresh link to the address we have on file.</p>
            {token && <button type="button" style={button} disabled={sent === 'busy'} onClick={ask}>{sent === 'busy' ? 'Sending…' : 'Email me a fresh link'}</button>}
          </>
        )}
    </div>
  );
}

/** No thank-you asset yet: never an error, always the title, the download
 *  (when there is a file) and the way to book time. */
function MinimalAccess({ data }: { data: AccessPayload }) {
  const m = data.lead_magnet;
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '48px 24px 80px', textAlign: 'center' }} data-testid="magnet-access-minimal">
      <h1 style={{ fontSize: 30, margin: '0 0 8px' }}>{m.title}</h1>
      {m.source_book_title && <p style={{ color: MUTED, margin: '0 0 24px' }}>From {m.source_book_title}{m.source_book_author ? ` by ${m.source_book_author}` : ''}</p>}
      {data.download_url
        ? <a href={data.download_url} rel="noreferrer" style={button} data-testid="magnet-access-download">Download your copy</a>
        : <p style={{ color: MUTED }}>Your copy is being prepared. Check back soon.</p>}
      {data.book_call && (
        <div style={{ marginTop: 32 }}>
          <p style={{ fontWeight: 600, margin: '0 0 10px' }}>Want to talk it through?</p>
          <a href={data.book_call.url} style={secondary} data-testid="magnet-access-book">{data.book_call.label || 'Book time with our team'}</a>
        </div>
      )}
      <CtaStack cta={{ ...data.cta, path3: null }} />
    </div>
  );
}

export default function MagnetAccessPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  // Read once: StrictMode's second mount must not lose the token.
  const [token] = useState(takeToken);
  const [state, setState] = useState<Phase>({ phase: 'loading' });
  const started = useRef(false);

  // The page must not pass its token on: no referrer from anything it loads or links to.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!slug || !token) { setState({ phase: 'expired' }); return; }
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/public/magnets/${encodeURIComponent(slug)}/access`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token }),
        });
        if (r.status === 410) { setState({ phase: 'expired' }); return; }
        if (r.status === 404) { setState({ phase: 'not_available' }); return; }
        if (!r.ok) { setState({ phase: 'error' }); return; }
        setState({ phase: 'ready', data: await r.json() as AccessPayload });
      } catch {
        setState({ phase: 'error' });
      }
    })();
  }, [slug, token]);

  if (state.phase === 'ready') {
    const { data } = state;
    const html = data.lead_magnet.thankyou_html;
    // The download box shows unless the thank-you body already carries this
    // person's download link ({{MAGNET_URL}} resolved by the worker).
    const needsBox = !!data.download_url && !(html ?? '').includes(data.download_url);
    const structured = structuredRenderEnabled() ? parseMagnetThankyouContent(data.lead_magnet.thankyou_content) : null;
    if (structured) {
      return (
        <>
          {data.download_url && <DownloadBox url={data.download_url} />}
          <MagnetStructuredThankyou content={structured} brand={data.brand} funnelUrls={data.funnel_urls} />
        </>
      );
    }
    if (isFullDoc(html)) {
      return (
        <>
          <MagnetHeader brand={data.brand} />
          {needsBox && <DownloadBox url={data.download_url!} />}
          <div className="magnet-thankyou-fulldoc" dangerouslySetInnerHTML={{ __html: html! }} />
          <MagnetFooter brand={data.brand} />
        </>
      );
    }
    return (
      <TakerShell logoUrl={data.brand?.logoWhiteUrl ?? data.brand?.logoUrl} logoAlt={data.brand?.name} logoHref={data.brand?.logoLinkUrl}>
        {html ? (
          <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 80px' }}>
            {needsBox && <DownloadBox url={data.download_url!} />}
            <div className="magnet-thankyou-body" dangerouslySetInnerHTML={{ __html: html }} />
            <CtaStack cta={data.cta} />
          </div>
        ) : <MinimalAccess data={data} />}
      </TakerShell>
    );
  }

  return (
    <TakerShell>
      {state.phase === 'loading' && <p style={{ textAlign: 'center', color: MUTED, padding: 64 }}>Loading…</p>}
      {state.phase === 'expired' && <Expired slug={slug} token={token} />}
      {state.phase === 'not_available' && (
        <div style={{ maxWidth: 560, margin: '64px auto', padding: '0 24px' }} data-testid="magnet-access-unavailable">
          <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>This resource isn't available</h1>
          <p style={{ lineHeight: 1.6 }}>It may have been retired. <a href="/offers" style={{ color: ACCENT }}>See what's available now</a>.</p>
        </div>
      )}
      {state.phase === 'error' && (
        <div style={{ maxWidth: 560, margin: '64px auto', padding: '0 24px' }}>
          <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Something went wrong</h1>
          <p style={{ lineHeight: 1.6 }}>This page didn't load. Please open the link from your email again.</p>
        </div>
      )}
    </TakerShell>
  );
}
